import type { EncryptedBlob } from '@shared/types';
import {
  messageArchiveApi,
  type MessageArchiveDestinationType,
  type MessageArchiveJob,
  type MessageArchiveMode,
  type MessageArchiveSegment
} from '../api/messageArchive';

export type MessageArchiveDestinationContext = {
  sourceCircleIdentityId: string;
  serverUrl?: string;
};

export type MessageArchiveSegmentWriteResult = {
  segmentId: string;
  archiveId: string;
  periodKey: string;
  state: 'current' | 'final';
  revision: number;
  createdAt?: string;
  updatedAt: string;
};

export interface MessageArchiveDestinationProvider {
  readonly type: MessageArchiveDestinationType;
  readonly destinationId: string;

  getJob(): Promise<MessageArchiveJob | null>;
  enableJob(payload: {
    destinationType?: MessageArchiveDestinationType;
    mode?: MessageArchiveMode;
    wrappedArchiveKey: EncryptedBlob | Record<string, unknown>;
    encryptedManifest?: EncryptedBlob | Record<string, unknown> | null;
  }): Promise<MessageArchiveJob>;
  updateManifest(payload: {
    archiveId: string;
    expectedRevision: number;
    encryptedManifest: EncryptedBlob | Record<string, unknown>;
    lastArchivedAt?: string | null;
  }): Promise<MessageArchiveJob>;
  updateWriter(archiveId: string): Promise<MessageArchiveJob>;
  listSegments(payload: {
    archiveId: string;
    afterPeriodKey?: string | null;
    limit?: number;
  }): Promise<MessageArchiveSegment[]>;
  putSegment(payload: {
    archiveId: string;
    periodKey: string;
    expectedRevision?: number | null;
    periodStartedAt?: string | null;
    periodEndedAt?: string | null;
    messageCount?: number;
    byteSize?: number;
    encryptedSegment: EncryptedBlob | Record<string, unknown>;
  }): Promise<MessageArchiveSegmentWriteResult>;
  finalizeSegment(payload: {
    archiveId: string;
    periodKey: string;
    expectedRevision: number;
  }): Promise<MessageArchiveSegmentWriteResult>;
}

function responseError(fallback: string, response: { status: string; error?: { message?: string } | null }): Error {
  return new Error(response.status === 'error' ? response.error?.message || fallback : fallback);
}

export function createCurrentCircleArchiveDestination(
  context: MessageArchiveDestinationContext
): MessageArchiveDestinationProvider {
  const sourceCircleIdentityId = context.sourceCircleIdentityId.trim();

  return {
    type: 'circle',
    destinationId: `circle:${sourceCircleIdentityId}`,

    async getJob() {
      const response = await messageArchiveApi.getJob(context.serverUrl, sourceCircleIdentityId);
      if (response.status !== 'ok') throw responseError('Failed to load message archive job', response);
      return response.result?.job || null;
    },

    async enableJob(payload) {
      const response = await messageArchiveApi.enableJob(payload, context.serverUrl, sourceCircleIdentityId);
      if (response.status !== 'ok' || !response.result?.job) {
        throw responseError('Failed to enable message archive', response);
      }
      return response.result.job;
    },

    async updateManifest(payload) {
      const response = await messageArchiveApi.updateManifest(payload, context.serverUrl, sourceCircleIdentityId);
      if (response.status !== 'ok' || !response.result?.job) {
        throw responseError('Failed to update archive manifest', response);
      }
      return response.result.job;
    },

    async updateWriter(archiveId) {
      const response = await messageArchiveApi.updateWriter(archiveId, context.serverUrl, sourceCircleIdentityId);
      if (response.status !== 'ok' || !response.result?.job) {
        throw responseError('Failed to update archive writer', response);
      }
      return response.result.job;
    },

    async listSegments(payload) {
      const response = await messageArchiveApi.listSegments(payload, context.serverUrl, sourceCircleIdentityId);
      if (response.status !== 'ok') throw responseError('Failed to load archive segments', response);
      return response.result?.segments || [];
    },

    async putSegment(payload) {
      const response = await messageArchiveApi.putSegment(payload, context.serverUrl, sourceCircleIdentityId);
      if (response.status !== 'ok' || !response.result?.segment) {
        throw responseError('Failed to write archive segment', response);
      }
      return response.result.segment;
    },

    async finalizeSegment(payload) {
      const response = await messageArchiveApi.finalizeSegment(payload, context.serverUrl, sourceCircleIdentityId);
      if (response.status !== 'ok' || !response.result?.segment) {
        throw responseError('Failed to finalize archive segment', response);
      }
      return response.result.segment;
    }
  };
}
